import type {
  Cart,
  DiscountedLineItemPrice,
  DiscountedLineItemPriceForQuantity,
  DiscountedProductPriceValue,
  LineItem,
  Maybe,
  ProductPrice,
  ProductVariant,
} from '~/types/GraphQL';

export default {
  getItems: (cart: Cart) => {
    if (!cart) {
      return [];
    }

    return cart.lineItems;
  },
  getTotalItems: (cart: Cart): number => {
    if (!cart) {
      return 0;
    }

    return cart.lineItems.reduce((previous, current) => previous + current.quantity, 0);
  },
  getShippingPrice: (cart: Cart): number => {
    const total = cart?.totalPrice?.centAmount;
    const shippingInfo = cart?.shippingInfo;
    const centAmount = shippingInfo?.shippingMethod?.zoneRates[0].shippingRates[0].freeAbove?.centAmount;

    if (!shippingInfo || !total || (centAmount && total >= centAmount)) {
      return 0;
    }

    return shippingInfo.price.centAmount / 100;
  },
  getCartTotalItems: (cart: Maybe<Cart | undefined>): number => {
    if (!cart) {
      return 0;
    }

    return cart.lineItems.reduce((previous, current) => previous + current.quantity, 0);
  },
  checkNewsletterSubscription: (cart: Maybe<Cart | undefined>) => cart?.custom?.customFieldsRaw?.find(({ name }) => name === 'newsletterRegistration')?.value ?? false,
  getTotals: (cart: Cart) => {
    if (!cart?.lineItems) {
      return {} as any;
    }

    function isLineItem(product: ProductVariant | LineItem): product is LineItem {
      // eslint-disable-next-line no-underscore-dangle
      return product.__typename === 'LineItem';
    }

    const getCartItemQty = (product: LineItem): number => product?.quantity || 0;

    const getPrice = (price: ProductPrice | DiscountedProductPriceValue | DiscountedLineItemPrice) => {
      return price ? price.value.centAmount / 100 : null;
    };

    const getDiscount = (product: ProductVariant | LineItem) => product.price?.discounted;

    const getSpecialPrice = (product: ProductVariant | LineItem) => {
      const discount = getDiscount(product);

      if (isLineItem(product)) {
        const { discountedPricePerQuantity } = product;
        const discountsLength = discountedPricePerQuantity.length;

        if (discountsLength > 0) {
          return getPrice(discountedPricePerQuantity[discountsLength - 1].discountedPrice);
        }
      }

      if (discount?.discount?.isActive) {
        return getPrice(discount);
      }

      return null;
    };

    const createPrice = (product: ProductVariant | LineItem): any => {
      if (!product) {
        return { regular: null, special: null };
      }

      const quantity = isLineItem(product) ? getCartItemQty(product) : 1;
      const regularPriceSingleItem = getPrice(product.price);
      const regularPrice = regularPriceSingleItem ? regularPriceSingleItem * quantity : regularPriceSingleItem;
      const specialPriceSingleItem = getSpecialPrice(product);
      const specialPrice = specialPriceSingleItem ? specialPriceSingleItem * quantity : specialPriceSingleItem;

      return {
        regular: regularPrice,
        special: specialPrice,
      };
    };

    const getCartItems = (cart: Cart): LineItem[] => {
      if (!cart) {
        return [];
      }

      return cart.lineItems;
    };

    const getCartItemPrice = (product: LineItem): any => createPrice(product);

    const getCartSubtotalPrice = (cart: Cart, selectSpecialPrices = false): number => {
      return getCartItems(cart).reduce((total, cartItem) => {
        const { special, regular } = getCartItemPrice(cartItem);
        const itemPrice = (selectSpecialPrices && special) || regular;

        return total + itemPrice;
      }, 0);
    };

    const calculatePrice = (acc: number, current: DiscountedLineItemPriceForQuantity, target = 'default') => {
      const { quantity = 1, discountedPrice } = current;

      const lineItemDiscount = discountedPrice?.includedDiscounts?.reduce((accDiscount, includedDiscount) => {
        if (target === 'employee') {
          if (includedDiscount?.discount?.key?.toLowerCase().includes('makik')) {
            return accDiscount + includedDiscount.discountedAmount.centAmount;
          }
        }
        else if (target === 'myKik') {
          if (includedDiscount?.discount?.key?.toLowerCase().includes('mykik')) {
            return accDiscount + includedDiscount.discountedAmount.centAmount;
          }
        }
        else if (target === 'voucher') {
          if (includedDiscount?.discountedAmount?.centAmount && includedDiscount?.discount?.requiresDiscountCode && includedDiscount?.discount?.key !== 'MAKIK' && !includedDiscount?.discount?.key?.toLowerCase().includes('mykik')) {
            return accDiscount + includedDiscount.discountedAmount.centAmount;
          }
        }
        else if (target === 'cart') {
          if (includedDiscount?.discountedAmount?.centAmount && !includedDiscount?.discount?.requiresDiscountCode && includedDiscount?.discount?.key !== 'MAKIK' && !includedDiscount?.discount?.key?.toLowerCase().includes('mykik')) {
            return accDiscount + includedDiscount.discountedAmount.centAmount;
          }
        }
        return accDiscount;
      }, 0);

      if (!lineItemDiscount) {
        return acc;
      }

      return acc + (quantity * lineItemDiscount);
    };

    const { lineItems = [] } = cart;

    const totalEmployeeDiscount = lineItems
      .flatMap(({ discountedPricePerQuantity }) => discountedPricePerQuantity)
      .reduce((acc, current) => calculatePrice(acc, current, 'employee'), 0);

    const totalmyKikDiscount = lineItems
      .flatMap(({ discountedPricePerQuantity }) => discountedPricePerQuantity)
      .reduce((acc, current) => calculatePrice(acc, current, 'myKik'), 0);

    const cartDiscount = lineItems
      .flatMap(({ discountedPricePerQuantity }) => discountedPricePerQuantity)
      .reduce((acc, current) => calculatePrice(acc, current, 'cart'), 0);

    const voucherDiscount = lineItems
      .flatMap(({ discountedPricePerQuantity }) => discountedPricePerQuantity)
      .reduce((acc, current) => calculatePrice(acc, current, 'voucher'), 0);

    const totalStrikePrice = lineItems.reduce((acc, { price, quantity }) => {
      const strikePrice = price?.custom?.customFieldsRaw?.find(({ name }) => name === 'strike_price')?.value.centAmount ?? 0;
      if (!strikePrice) {
        return acc;
      }

      if (price.value.centAmount > strikePrice) {
        return acc;
      }

      const saving = strikePrice - price.value.centAmount;

      return acc + quantity * saving;
    }, 0);

    const totalDeposit = lineItems.reduce((acc, { price, quantity }) => {
      const deposit = price?.custom?.customFieldsRaw?.find(({ name }) => name === 'deposit_price')?.value.centAmount ?? 0;
      if (!deposit) {
        return acc;
      }

      return acc + quantity * deposit;
    }, 0);

    return {
      getItemPrice: getCartItemPrice,
      total: cart.totalPrice.centAmount / 100,
      subtotal: getCartSubtotalPrice(cart),
      special: getCartSubtotalPrice(cart, true),
      totalStrikePrice: totalStrikePrice * 10 ** -cart.totalPrice.fractionDigits,
      cartDiscount: cartDiscount * 10 ** -cart.totalPrice.fractionDigits,
      voucherDiscount: voucherDiscount * 10 ** -cart.totalPrice.fractionDigits,
      totalEmployeeDiscount: totalEmployeeDiscount * 10 ** -cart.totalPrice.fractionDigits,
      totalmyKikDiscount: totalmyKikDiscount * 10 ** -cart.totalPrice.fractionDigits,
      totalDeposit: totalDeposit * 10 ** -cart.totalPrice.fractionDigits,
    };
  },
  availableQuantities: (stock: number, min: number, quantity: number) => {
    if (stock < min) {
      return [quantity];
    }

    const range = Array.from({ length: stock - min + 1 }, (_, i) => min + i);

    // Include current quantity if it's below minimum and not already included
    if (quantity < min && !range.includes(quantity)) {
      range.unshift(quantity); // Add at the beginning for UX continuity
    }

    // Optional: sort the final list ascending
    return Array.from(new Set(range)).sort((a, b) => a - b);
  },
  getDiscounts: (cart: Cart) => cart?.discountCodes,
  getCoupons: (cart: Maybe<Cart>) => cart?.discountCodes?.map(({ discountCode }) => discountCode),
  isActive: (cart: Cart) => cart.cartState === 'Active',
  getItemQtyBySku: (cart: Cart, sku: string) => cart?.lineItems?.find(({ variant }) => variant?.sku === sku)?.quantity ?? 0,
  isPackstationShipping: (cart: Cart) => cart.shippingInfo?.shippingMethod?.custom?.customFieldsRaw?.some(customField => customField.name === 'type' && customField.value === 'packstation'),
  getLoyaltyCouponCode: (cart: Maybe<Cart>) => cart?.custom?.customFieldsRaw?.find(loyaltyCouponCode => loyaltyCouponCode.name === 'loyaltyCouponCode'),
};
